// ─── Temps réel (SignalR) ──────────────────────────────────────────
//
// Une seule connexion par session, montée par <RealtimeProvider> (posé dans
// Dashboard.jsx, donc après connexion, et démonté à la déconnexion). Le hub
// (`Hubs/FonxtioSignalR.cs`, `/hubs/monitor`) diffuse aujourd'hui tous ses
// événements à `Clients.All` — AUCUNE portée de visibilité n'est appliquée
// côté serveur sur ces diffusions.
//
// RÈGLE DE SÉCURITÉ NON NÉGOCIABLE (cf. section « Portée de visibilité » de
// CLAUDE.md) : un événement n'est jamais rendu tel quel. Il ne sert qu'à
// déclencher un refetch via les endpoints REST déjà filtrés par
// PermissionService (GetAccessibleAgentIdsAsync / CanAccessAgentAsync). Un
// utilisateur sans accès à l'appareil concerné reçoit quand même l'événement
// (payload compris), mais son refetch scoped ne lui montrera jamais la
// ressource — donc aucun toast, aucun badge, aucune ligne de tableau ne la
// révèle. Les handlers passés à `useRealtime` ne doivent JAMAIS afficher les
// arguments de l'événement directement : ils ne servent qu'à savoir QUOI
// rafraîchir (quel agent, quelle liste), jamais à afficher un contenu.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { ensureFreshToken, getAuthToken } from './api.jsx';

const RealtimeContext = createContext(null);

// Événements émis aujourd'hui par le hub (cf. `grep -rn 'SendAsync("' Services/ Controllers/`).
// Les frames RDP (`RdpFrameReady`, groupe dédié) ne sont pas listées ici : hors sujet.
const HUB_EVENTS = [
  'NewAlert',
  'AgentStatusChanged',
  'Heartbeat',
  'InventoryUpdated',
  'CommandCompleted',
  'CommandCreated',
  'ProvisionClaimed',
  'RemoteSupportChanged',
];

export function RealtimeProvider({ children }) {
  // 'connected' | 'reconnecting' | 'disconnected'
  const [state, setState] = useState('disconnected');
  const listenersRef = useRef(new Map()); // eventName -> Set<handler>
  const connectionRef = useRef(null);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return undefined;

    const conn = new signalR.HubConnectionBuilder()
      // Le jeton est relu à CHAQUE (re)connexion : après un renouvellement par code 2FA,
      // la reprise part avec le nouveau jeton (et demande le code si l'ancien a expiré).
      .withUrl('/hubs/monitor', { accessTokenFactory: () => ensureFreshToken() })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 20000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();
    connectionRef.current = conn;

    // Salut du hub à la connexion (OnConnectedAsync) : sans handler, le client
    // SignalR journalise un avertissement à chaque (re)connexion.
    conn.on('Connected', () => {});

    HUB_EVENTS.forEach((name) => {
      conn.on(name, (...args) => {
        const set = listenersRef.current.get(name);
        if (!set) return;
        // Copie défensive : un handler peut se désabonner pendant l'itération
        // (changement de vue déclenché par l'événement lui-même).
        Array.from(set).forEach((fn) => {
          try {
            fn(...args);
          } catch (e) {
            console.error('[realtime]', name, e);
          }
        });
      });
    });

    conn.onreconnecting(() => setState('reconnecting'));
    conn.onreconnected(() => setState('connected'));
    // La reconnexion automatique abandonne après son dernier délai (30 s). Un jeton refusé
    // (renouvellement en attente) ne doit pas laisser le temps réel mort jusqu'au rechargement :
    // on retente périodiquement tant que le composant est monté.
    let stopped = false;
    let retryTimer = null;
    const startLoop = () => {
      conn
        .start()
        .then(() => setState('connected'))
        .catch(() => {
          setState('disconnected');
          if (!stopped && connectionRef.current === conn) retryTimer = setTimeout(startLoop, 15000);
        });
    };
    conn.onclose(() => {
      setState('disconnected');
      if (!stopped && connectionRef.current === conn) retryTimer = setTimeout(startLoop, 15000);
    });

    startLoop();

    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      connectionRef.current = null;
      conn.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = useCallback((eventName, handler) => {
    let set = listenersRef.current.get(eventName);
    if (!set) {
      set = new Set();
      listenersRef.current.set(eventName, set);
    }
    set.add(handler);
    return () => set.delete(handler);
  }, []);

  const disconnect = useCallback(() => {
    const conn = connectionRef.current;
    connectionRef.current = null; // coupure voulue : la boucle de reprise ne relance rien
    if (conn) conn.stop();
    setState('disconnected');
  }, []);

  return (
    <RealtimeContext.Provider value={{ state, subscribe, disconnect }}>
      {children}
    </RealtimeContext.Provider>
  );
}

/** État de la connexion temps réel : 'connected' | 'reconnecting' | 'disconnected'. */
export function useRealtimeState() {
  const ctx = useContext(RealtimeContext);
  return ctx ? ctx.state : 'disconnected';
}

/** Coupe explicitement la connexion (déconnexion utilisateur). */
export function useRealtimeDisconnect() {
  const ctx = useContext(RealtimeContext);
  return ctx ? ctx.disconnect : () => {};
}

/**
 * S'abonne à un événement du hub tant que le composant est monté.
 *
 * Le handler ne doit être qu'un SIGNAL D'INVALIDATION (déclenche un refetch
 * scoped) — jamais un moyen d'afficher les arguments reçus. Cf. l'en-tête du
 * fichier.
 */
export function useRealtime(eventName, handler) {
  const ctx = useContext(RealtimeContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    if (!ctx) return undefined;
    return ctx.subscribe(eventName, (...args) => handlerRef.current(...args));
  }, [ctx, eventName]);
}

/**
 * Regroupe des déclenchements rapprochés en un seul appel, au plus un par
 * `delay` ms — front de fin, sans réinitialiser le délai à chaque appel
 * (contrairement à un debounce classique). Une rafale d'événements
 * `Heartbeat` sur un grand parc arrive en continu ; un debounce classique ne
 * se déclencherait jamais tant que le flux ne s'arrête pas. Ici, un appel
 * pendant que la fenêtre est déjà programmée est ignoré : au pire un
 * rafraîchissement toutes les `delay` ms.
 */
export function useCoalesced(fn, delay = 1500) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const pendingRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return useCallback(() => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    timerRef.current = setTimeout(() => {
      pendingRef.current = false;
      fnRef.current();
    }, delay);
  }, [delay]);
}
